import { useCallback, useRef } from "react";

import { DateTime } from "@bps/date-time";
import { PagingOptions } from "@libs/api/dtos/index.ts";
import {
  CalendarEventStatus,
  GetCalendarEventsDto
} from "@libs/gateways/booking/BookingGateway.dtos.ts";
import { QueryResult } from "@libs/utils/promise-observable/promise-observable.utils.ts";
import { CalendarEvent } from "@stores/booking/models/CalendarEvent.ts";
import { useStores } from "@stores/hooks/useStores.ts";

import { durationMap } from "../utils.ts";
import { ApptinfoFilterType } from "./AppointmentInformationModalInner.tsx";
import { PrefAndInformationPivotTabTypes } from "./AppointmentInformationPivotTabs.types.ts";
import { preloadClaims } from "./AppointmentInformationTable.utils.tsx";

export const useGetCalendarEvents = (
  props: {
    claimId: string | undefined;
    contactId: string;
    selectedTab: PrefAndInformationPivotTabTypes;
  },
  filter: ApptinfoFilterType
) => {
  const { claimId, contactId, selectedTab } = props;
  // tracks whether claims have already been loaded, ensuring they don't get loaded multiple times
  const isLoadedRef = useRef(false);
  const root = useStores();
  const { acc, booking } = root;

  const getItems = useCallback(
    async (query: PagingOptions): Promise<QueryResult<CalendarEvent>> => {
      const nowIso = DateTime.now().toISO();
      const isPast = selectedTab === PrefAndInformationPivotTabTypes.Past;

      const args: GetCalendarEventsDto = {
        ...query,
        attendees: [contactId],
        statuses: [
          CalendarEventStatus.Confirmed,
          CalendarEventStatus.Cancelled
        ],
        endsAfter: isPast ? undefined : nowIso,
        endTime: isPast ? nowIso : undefined,
        sortDescending: isPast
      };

      const filterDate = DateTime.fromJSDate(filter.apptDate);
      if (filterDate?.isValid) {
        args.endsAfter = undefined;
        args.startTime = filterDate.startOf("day").toISO();
        args.endTime = filterDate.endOf("day").toISO();
      }

      if (filter.status?.length) {
        args.appointmentStatuses = filter.status.filter(
          status => status !== CalendarEventStatus.Cancelled
        );
        if (
          filter.status.some(status => status === CalendarEventStatus.Cancelled)
        ) {
          args.statuses = [CalendarEventStatus.Cancelled];
        }
      }

      if (filter.apptType?.length) {
        args.appointmentTypes = filter.apptType;
      }

      if (filter.providerId) {
        args.allAttendees = [contactId, filter.providerId];
      }

      if (filter.durationRange) {
        const { min, max } = durationMap[filter.durationRange];
        args.minDuration = min;
        args.maxDuration = max;
      }

      if (claimId) {
        if (!isLoadedRef.current) {
          const [episodeOfCareId] = await Promise.all([
            acc
              .getClaimEpisodesOfCare({ claimIds: [claimId] })
              .then(ceocs =>
                ceocs.length > 0 ? ceocs[0].episodeOfCareId : undefined
              ),
            acc.getClaim(claimId)
          ]);

          if (episodeOfCareId) {
            args.episodeOfCareIds = [episodeOfCareId];
          }
        } else {
          const ceoc = Array.from(acc.claimEpisodeOfCareMap.values()).find(
            ceoc => ceoc.claimId === claimId
          );
          if (ceoc) {
            args.episodeOfCareIds = [ceoc.episodeOfCareId];
          }
        }
      } else if (filter.claim) {
        const claimIds = Array.from(acc.claimsMap.values())
          .filter(
            claim =>
              claim.patientId === contactId &&
              claim.claimNumber
                ?.toLowerCase()
                .includes(filter.claim?.toLowerCase() ?? "")
          )
          .map(claim => claim.id);

        const episodeOfCareIds = Array.from(acc.claimEpisodeOfCareMap.values())
          .filter(ceoc => claimIds.includes(ceoc.claimId))
          .map(ceoc => ceoc.episodeOfCareId);

        if (!episodeOfCareIds.length) {
          // We have to manually return no results here
          return { results: [], skip: 0, take: 0 };
        } else {
          args.episodeOfCareIds = Array.from(acc.claimEpisodeOfCareMap.values())
            .filter(ceoc => claimIds.includes(ceoc.claimId))
            .map(ceoc => ceoc.episodeOfCareId);
        }
      }

      const loadClaims =
        claimId || isLoadedRef.current
          ? undefined
          : preloadClaims(root, contactId).then(() => {
              if (!isLoadedRef.current) {
                isLoadedRef.current = true;
              }
            });

      return Promise.all([booking.getCalendarEvents(args), loadClaims]).then(
        ([result]) => result
      );
    },
    [
      root,
      booking,
      contactId,
      selectedTab,
      filter.status,
      filter.apptDate,
      filter.apptType,
      filter.providerId,
      filter.durationRange,
      filter.claim,
      acc,
      claimId
    ]
  );

  return getItems;
};
